import { HlmStage } from "../utils/hlmStageHelpers";
import { WarehouseOrderType } from "@features/assets-feature/constants";

export const rackOrderStatuses = [
  { name: "Ordered" },
  { name: "Staging" },
  { name: "Rack/Stack" },
  { name: "Cable/Label" },
  { name: "Config/QC" },
  { name: "Shipped" },
];

export const kitOrderStatuses = [
  { name: "Ordered" },
  { name: "Picked" },
  { name: "In Config" },
  { name: "Shipped" },
];

export type IntegrationCenterOrderStatus = "completed" | "current" | "pending";

export const getHlmStatus = (
  index: number,
  hlmStages: HlmStage[],
): IntegrationCenterOrderStatus => {
  const hlmStage = hlmStages[index];
  if (hlmStage.status === "completed") {
    return "completed";
  } else if (hlmStage.status === "in-progress") {
    return "current";
  } else {
    return "pending";
  }
};

export const getTransferIntegrationStatus = (
  index: number,
  currentStepIndex: number,
  shippedStepIndex: number,
  allTrackingNumbersComplete: boolean,
): IntegrationCenterOrderStatus => {
  if (index === shippedStepIndex) {
    // Special case for Transfer, Integration: "Shipped" step is only completed when all tracking exists
    const isCompleted =
      currentStepIndex > shippedStepIndex || allTrackingNumbersComplete;
    const isCurrent = currentStepIndex === shippedStepIndex;
    if (isCompleted) {
      return "completed";
    } else if (isCurrent) {
      return "current";
    } else {
      return "pending";
    }
  }

  if (index < currentStepIndex) {
    return "completed";
  } else if (index === currentStepIndex) {
    return "current";
  } else {
    return "pending";
  }
};

export const getRackAndStackStatus = (
  index: number,
  currentStep: number,
): IntegrationCenterOrderStatus => {
  if (index < currentStep) {
    return "completed";
  } else if (index === currentStep) {
    return "current";
  } else {
    return "pending";
  }
};

export const getLastCompletedStatus = (orderStatus?: string): string => {
  if (!orderStatus) return "";
  const steps = orderStatus.split(",").map((s) => s.trim());
  return steps.at(-1) ?? "";
};

export const normalizeTransferIntegrationOrderStatus = (
  orderStatus: string | undefined,
  orderType: string | undefined,
): string => {
  let normalizedOrderStatus = (orderStatus || "").toLowerCase().trim();
  const isTransferOrder =
    orderType?.toLowerCase() === WarehouseOrderType.pickNShip.toLowerCase();

  if (isTransferOrder && normalizedOrderStatus === "in config") {
    normalizedOrderStatus = "picked";
  }

  return normalizedOrderStatus;
};

export const getCurrentOrderStatus = (
  orderStatus: string | undefined,
  orderType: string | undefined,
): string => {
  let stepList: { name: string }[];
  if (
    orderType === WarehouseOrderType.rack ||
    orderType === WarehouseOrderType.miniRack
  ) {
    stepList = rackOrderStatuses;
  } else if (orderType === WarehouseOrderType.kit) {
    stepList = kitOrderStatuses;
  } else {
    // fallback: for other types, just return the last status (legacy behavior)
    if (!orderStatus) return "";
    const statuses = orderStatus.split(",").map((s) => s.trim());
    return statuses.at(-1) ?? "";
  }

  if (!orderStatus?.trim()) {
    return stepList[0].name;
  }

  const stepNames = stepList.map((step) => step.name.toLowerCase());
  const completed = orderStatus
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const lastCompletedIdx = Math.max(
    ...completed
      .map((status) => stepNames.indexOf(status))
      .filter((idx) => idx !== -1),
    -1,
  );

  const inProgressIdx =
    lastCompletedIdx + 1 < stepList.length
      ? lastCompletedIdx + 1
      : stepList.length - 1;

  return stepList[inProgressIdx].name;
};

export const getNextOrderStatus = (
  currentStatus: string,
  orderType: string,
): string => {
  let stepList: { name: string }[];
  if (
    orderType === WarehouseOrderType.rack ||
    orderType === WarehouseOrderType.miniRack
  ) {
    stepList = rackOrderStatuses;
  } else if (orderType === WarehouseOrderType.kit) {
    stepList = kitOrderStatuses;
  } else {
    return "";
  }

  const idx = stepList.findIndex(
    (step) => step.name.toLowerCase() === (currentStatus || "").toLowerCase(),
  );
  if (idx === -1 || idx === stepList.length - 1) {
    return "";
  }
  return stepList[idx + 1].name;
};

export const getLastCompletedIndex = (
  orderStatus: string | undefined,
  statuses: { name: string }[],
) => {
  if (!orderStatus) return -1;
  const completed = orderStatus
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const stepNames = statuses.map((s) => s.name.toLowerCase());
  return Math.max(
    ...completed
      .map((status) => stepNames.indexOf(status))
      .filter((i) => i !== -1),
    -1,
  );
};
